import { Avatar, Box, Card, Menu, Text } from '@mantine/core'
import { IconActivity, IconChevronDown, IconDisc, IconLock, IconPencil } from '@tabler/icons-react'
import type { ManagedStudent } from 'api/arcMosaico/types'
import { Button } from 'components/Button'
import { getInitials } from 'helpers'
import { useFeatureToggle } from 'hooks/useFeatureToggle'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { demoStudentDiscPath } from 'arcdisk'

interface Props {
  student: ManagedStudent
  onEdit: (student: ManagedStudent) => void
  onResetPassword: (student: ManagedStudent) => void
}

export function StudentCard({ student, onEdit, onResetPassword }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isArc = useFeatureToggle('arc-mosaico-project') as boolean
  const gradeName = student.grades?.[0]?.name || null
  // ArcDisk demo students: their disc is the ArcDisk student page (SPA route)
  const demoDiscPath = demoStudentDiscPath(student.id)

  return (
    <Card
      withBorder
      p='sm'
      radius='md'
      sx={theme => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing.xs,
      })}
    >
      <Avatar src={student.avatarUrl} size={48} radius='xl' color='gray.5'>
        {getInitials(`${student.firstName} ${student.lastName}`)}
      </Avatar>

      <Text fw={600} size='sm' ta='center' lineClamp={1}>
        {student.firstName} {student.lastName}
      </Text>

      {gradeName && (
        <Box ta='center'>
          <Text size='xs' color='dimmed'>
            {t('manage_students.grade_label')}
          </Text>
          <Text size='sm'>{gradeName}</Text>
        </Box>
      )}

      <Menu shadow='md' width={200} position='bottom' withinPortal>
        <Menu.Target>
          <Button fullWidth variant='light' rightIcon={<IconChevronDown size={18} stroke={1.5} />} pr={12}>
            {t('manage_students.actions')}
          </Button>
        </Menu.Target>

        <Menu.Dropdown>
          {student.permissions.editProfile && (
            <Menu.Item icon={<IconPencil size={16} />} onClick={() => onEdit(student)}>
              {t('manage_students.action_edit_profile')}
            </Menu.Item>
          )}
          {student.permissions.resetPassword && (
            <Menu.Item icon={<IconLock size={16} />} onClick={() => onResetPassword(student)}>
              {t('manage_students.action_reset_password')}
            </Menu.Item>
          )}
          {student.permissions.viewDisc && demoDiscPath && (
            <Menu.Item icon={<IconDisc size={16} />} onClick={() => navigate(demoDiscPath)}>
              {t('manage_students.action_view_disc')}
            </Menu.Item>
          )}
          {student.permissions.viewDisc && !demoDiscPath && (
            <Menu.Item
              icon={<IconDisc size={16} />}
              component='a'
              href={`/students/${student.id}/student_mosaics`}
            >
              {t('manage_students.action_view_disc')}
            </Menu.Item>
          )}
          {isArc && !demoDiscPath && (
            <Menu.Item
              icon={<IconActivity size={16} />}
              onClick={() =>
                navigate(`/teacher/manage-students/${student.id}/activity`, { state: { student } })
              }
            >
              {t('manage_students.action_view_activity')}
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    </Card>
  )
}
